import type { InvoiceEntity } from "../api/types";
import { formatSummaryCurrency } from "./currency-logic";
import { sameMoney } from "./money";

type InvoiceAmounts = Pick<InvoiceEntity, "amount" | "amountMax">;

/**
 * An invoice's amount as one string — or, when it carries an `amountMax`, the
 * range a payment is matched against ("100,00 kr – 250,50 kr"), as the
 * invoice view's Amount row shows it. Null when the invoice has no amount at
 * all.
 */
export const formatInvoiceAmount = (invoice: InvoiceAmounts): string | null => {
  const { amount } = invoice;
  const max = invoice.amountMax ?? null;
  if (amount && max)
    return `${formatSummaryCurrency(amount)} – ${formatSummaryCurrency(max)}`;
  if (amount) return formatSummaryCurrency(amount);
  // A max with no amount matches nothing by amount on the server; still say
  // what is stored rather than hide it.
  if (max) return `up to ${formatSummaryCurrency(max)}`;
  return null;
};

/** The figures an Invoices list row or tile shows for an invoice's amount. */
export type InvoiceListAmount = {
  /** The headline figure, in the amount's usual place and weight. */
  main: string;
  /** The minimum payment, shown faded beside `main` ("min 1 030,00 kr"). */
  minimum: string | null;
};

/**
 * How the Invoices list and tiles show an invoice's amount. With both an
 * amount and an `amountMax` (a card invoice: minimum payment and full
 * balance), the full balance is the headline and the minimum rides along
 * faded — reading the minimum as the bill understates it. A lone amount shows
 * as itself; a lone max as "up to …". Null when there is no amount at all.
 */
export const invoiceListAmount = (
  invoice: InvoiceAmounts,
): InvoiceListAmount | null => {
  const { amount } = invoice;
  const max = invoice.amountMax ?? null;
  if (amount && max)
    return {
      main: formatSummaryCurrency(max),
      minimum: sameMoney(amount, max)
        ? null
        : `min ${formatSummaryCurrency(amount)}`,
    };
  if (amount) return { main: formatSummaryCurrency(amount), minimum: null };
  if (max)
    return { main: `up to ${formatSummaryCurrency(max)}`, minimum: null };
  return null;
};
